import { makeObservable, observable } from 'mobx';
import type { Aggregate } from 'edit/Aggregate';
import type { RaiseEvent } from 'api/node_definitions/endpoints/types';
import type { EditableNodeDefinition } from './EditableNodeDefinition';

// Monotonic per-instance key for React list rendering. Raise events have no stable server id, so each instance gets a unique
// key at construction that never changes across add/remove.
let nextRaiseEventKey = 0;

/**
 * One definition-level raise-event link, as a reactive child of an `EditableNodeDefinition`. The parent→child relationship is
 * owned by a shared `Aggregate` that both sides hold; this child routes its self-delete through it.
 *
 * `path` is a `"zone|name"` string the backend resolves. `status` is part of the wire shape but the UI doesn't edit it, so
 * it's held privately and round-tripped by `toSave` — a loaded raise event mustn't lose its status.
 */
export class EditableRaiseEvent {
  private readonly _aggregate: Aggregate<EditableNodeDefinition, EditableRaiseEvent>;
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `raise-event-${(nextRaiseEventKey += 1)}`;
  path: string | null = null;
  precondition: string | null = null;
  raiserComment: string | null = null;
  // Held but not surfaced in the UI — preserved across the edit so a loaded raise event round-trips its status.
  private readonly _status: string | null;

  /**
   * @param aggregate the shared parent→children owner; self-delete routes through it.
   * @param wire when present, seeds from a loaded raise event; when absent, a fresh empty event.
   */
  constructor(aggregate: Aggregate<EditableNodeDefinition, EditableRaiseEvent>, wire?: RaiseEvent) {
    this._aggregate = aggregate;
    this._status = wire?.status ?? null;
    makeObservable(this, {
      path: observable,
      precondition: observable,
      raiserComment: observable,
    });
    if (wire !== undefined) {
      this.path = wire.path;
      this.raiserComment = wire.raiser_comment ?? null;
      this.precondition = wire.precondition ?? null;
    }
  }

  /** A one-line summary for the card subtitle, or `null` before an event is chosen. */
  get summary(): string | null {
    if (!this.path) return null;
    const name = this.path.split('|')[1] ?? this.path;
    return this.raiserComment ? `${name} — "${this.raiserComment}"` : name;
  }

  /** Remove this raise event from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /** Project to the wire shape, preserving the round-tripped `status`. */
  toSave(): RaiseEvent {
    return {
      path: this.path,
      raiser_comment: this.raiserComment,
      status: this._status,
      precondition: this.precondition,
    };
  }
}
